/* ============================================================
   Floral · THC Laws by State — interactive map controller
   - Reads window.FloralThcStateData (injected by the section)
   - Colors each <path> by status
   - Renders state card on click; shows hover tooltip
   ============================================================ */
(function(){
  'use strict';

  function init(root){
    if(!root) return;
    var data = window.FloralThcStateData || {};
    var svgHost = root.querySelector('.flmap__svg');
    var svg = svgHost && svgHost.querySelector('svg');
    var card = root.querySelector('.flmap__card');
    var tooltip = root.querySelector('.flmap__tooltip');
    var defaultState = root.dataset.defaultState || 'IN';
    if(!svg) return;

    // Strip default fill + dimensional attrs so CSS owns sizing/color
    svg.removeAttribute('fill');
    svg.removeAttribute('width');
    svg.removeAttribute('height');

    // Inject crosshatch pattern for "pending sunset" status
    if(!svg.querySelector('#flmap-hatch-pending')){
      var svgNS = 'http://www.w3.org/2000/svg';
      var defs = svg.querySelector('defs') || svg.insertBefore(document.createElementNS(svgNS,'defs'), svg.firstChild);
      var pat = document.createElementNS(svgNS,'pattern');
      pat.setAttribute('id','flmap-hatch-pending');
      pat.setAttribute('patternUnits','userSpaceOnUse');
      pat.setAttribute('width','6'); pat.setAttribute('height','6');
      pat.setAttribute('patternTransform','rotate(45)');
      var bg = document.createElementNS(svgNS,'rect');
      bg.setAttribute('width','6'); bg.setAttribute('height','6');
      bg.setAttribute('fill','#7BA98E');
      var ln = document.createElementNS(svgNS,'line');
      ln.setAttribute('x1','0'); ln.setAttribute('y1','0');
      ln.setAttribute('x2','0'); ln.setAttribute('y2','6');
      ln.setAttribute('stroke','#ffffff'); ln.setAttribute('stroke-width','1.4');
      pat.appendChild(bg); pat.appendChild(ln);
      defs.appendChild(pat);
    }

    // Build state lookup keyed by 2-letter code.
    // Wikipedia map uses class="ak", class="al" etc. for state shapes;
    // borders are like "al-fl" — those have hyphens, so we skip multi-class
    // and only target single-class state paths.
    Object.keys(data).forEach(function(code){
      var cls = code.toLowerCase();
      // CSS selector pattern: path that has class equal to the code (no hyphen)
      var paths = svg.querySelectorAll('path[class="' + cls + '"]');
      if(!paths.length) return;
      var d = data[code];
      paths.forEach(function(p){
        p.setAttribute('data-status', d.status);
        p.setAttribute('data-state', code);
        p.setAttribute('tabindex', '0');
        p.setAttribute('role', 'button');
        p.setAttribute('aria-label', d.name + ' — ' + d.pill);
        p.style.cursor = 'pointer';
        p.addEventListener('click', function(){ select(code); });
        p.addEventListener('keydown', function(e){
          if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); select(code); }
        });
        if(tooltip){
          p.addEventListener('mousemove', function(e){
            var rect = root.querySelector('.flmap__map').getBoundingClientRect();
            tooltip.textContent = d.name;
            tooltip.style.left = (e.clientX - rect.left) + 'px';
            tooltip.style.top  = (e.clientY - rect.top)  + 'px';
            tooltip.style.opacity = '1';
          });
          p.addEventListener('mouseleave', function(){ tooltip.style.opacity = '0'; });
        }
      });
    });

    function select(code){
      if(!card) return;
      var d = data[code];
      if(!d){
        card.innerHTML = '<div class="flmap__card-lead">State detail not yet published for ' + code + '.</div>';
        return;
      }
      // Highlight active path (a state may have multiple shape paths)
      svg.querySelectorAll('path.is-active').forEach(function(p){ p.classList.remove('is-active'); });
      svg.querySelectorAll('path[data-state="' + code + '"]').forEach(function(p){ p.classList.add('is-active'); });

      card.innerHTML = renderCard(d);

      // Always scroll to the card so the user sees that something happened
      try { card.scrollIntoView({behavior:'smooth', block:'start'}); }
      catch(e){ window.scrollTo(0, card.offsetTop - 80); }

      // Pulse the card border briefly for visible feedback
      card.classList.remove('is-pulse');
      // force reflow so animation restarts on subsequent clicks
      void card.offsetWidth;
      card.classList.add('is-pulse');
      setTimeout(function(){ card.classList.remove('is-pulse'); }, 900);
    }

    function renderCard(d){
      var ships = d.floralShips
        ? '<div class="flmap__ships"><span>🌾</span><span><strong>Floral ships to ' + escapeHtml(d.name) + '.</strong> &nbsp;<a href="/collections/all">See what\'s available →</a></span></div>'
        : '';

      var statuteLink = d.statuteLink
        ? ' &nbsp;·&nbsp; <a href="' + d.statuteLink + '" rel="nofollow noopener" target="_blank">View statute →</a>'
        : '';

      return ''
        + '<div class="flmap__card-head">'
        +   '<h2>' + escapeHtml(d.name) + '</h2>'
        +   '<span class="flmap__pill" data-status="' + d.status + '">' + escapeHtml(d.pill) + '</span>'
        + '</div>'
        + '<div class="flmap__card-lead">' + escapeHtml(d.blurb) + '</div>'
        + '<div class="flmap__col">'
        +   '<h3>Cannabinoid Status</h3>'
        +   '<ul>'
        +     '<li><strong>THC cap:</strong> ' + escapeHtml(d.cap) + '</li>'
        +     '<li><strong>Statute / Reg:</strong> ' + escapeHtml(d.cite) + '</li>'
        +     '<li><strong>Age:</strong> ' + escapeHtml(d.age) + '</li>'
        +     '<li><strong>Shipping:</strong> ' + escapeHtml(d.ship) + '</li>'
        +   '</ul>'
        +   '<div class="flmap__cite"><cite>' + escapeHtml(d.cite) + '</cite>' + statuteLink + '</div>'
        + '</div>'
        + '<div class="flmap__col">'
        +   '<h3>Registry &amp; Enforcement</h3>'
        +   '<ul>'
        +     (d.registry ? '<li><a href="' + d.registry + '" rel="nofollow noopener" target="_blank">' + escapeHtml(d.registryLabel || 'State Hemp Program') + ' →</a></li>' : '')
        +     '<li>Federal Section 781 — effective Nov 12, 2026</li>'
        +   '</ul>'
        +   ships
        + '</div>'
        + '<div class="flmap__card-meta">'
        +   '<div>Last reviewed: <strong>' + escapeHtml(d.reviewed || '—') + '</strong> &nbsp;·&nbsp; Statute current as of: <strong>' + escapeHtml(d.statuteAsOf || '—') + '</strong></div>'
        +   '<button class="flmap__cite-btn" type="button" data-state="' + d.code + '">Cite this page</button>'
        + '</div>';
    }

    function escapeHtml(s){
      return String(s == null ? '' : s)
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
        .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
    }

    // "Cite this page" copy-to-clipboard
    root.addEventListener('click', function(e){
      var btn = e.target.closest('.flmap__cite-btn');
      if(!btn) return;
      var code = btn.dataset.state;
      var d = data[code];
      if(!d) return;
      var citation = 'Floral Beverages Legal Research Team, "Is Delta-9 THC Legal in '
        + d.name + '?" (last reviewed ' + d.reviewed + '), '
        + window.location.origin + '/pages/thc-laws-by-state ('
        + 'citing ' + d.cite + ').';
      try {
        navigator.clipboard.writeText(citation);
        btn.textContent = 'Copied ✓';
        setTimeout(function(){ btn.textContent = 'Cite this page'; }, 2000);
      } catch(err){ console.warn('clipboard write failed', err); }
    });

    // Initial render — default state
    if(data[defaultState]) select(defaultState);
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', function(){
      document.querySelectorAll('[data-flmap-root]').forEach(init);
    });
  } else {
    document.querySelectorAll('[data-flmap-root]').forEach(init);
  }
})();
